import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { setSession } from '../lib/auth';
import { API_BASE } from '../lib/api';
import { takeRememberedVerifier } from '../lib/pkce';

export function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const code = params.get('code');
      const state = params.get('state');
      const oauthError = params.get('error');

      if (oauthError) {
        setError(`WorkOS returned an error: ${oauthError}`);
        return;
      }
      if (!code || !state) {
        setError('Missing code or state in callback URL.');
        return;
      }
      const codeVerifier = takeRememberedVerifier(state);
      if (!codeVerifier) {
        setError('Sign-in state expired or was not initiated from this browser.');
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/auth/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, state, code_verifier: codeVerifier }),
        });
        if (!res.ok) {
          let detail = '';
          try {
            detail = (await res.json()).message ?? '';
          } catch {
            // ignore
          }
          setError(`Sign-in failed (HTTP ${res.status}${detail ? ': ' + detail : ''}).`);
          return;
        }
        const data = (await res.json()) as {
          user: { id: string; email: string };
          access_token: string;
          access_token_expires_at: string;
          refresh_token: string;
        };
        if (cancelled) return;
        setSession({
          token: data.access_token,
          userId: data.user.id,
          email: data.user.email,
          orgId: null,
          role: 'member',
        });
        navigate('/library', { replace: true });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Sign-in failed');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [params, navigate]);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 text-sm">
        <p className="text-red-400">{error}</p>
        <button
          type="button"
          onClick={() => navigate('/login', { replace: true })}
          className="border px-3 py-1 hover:bg-[var(--color-accent-soft)]"
        >
          back to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center text-sm text-[var(--color-muted)]">
      signing you in…
    </div>
  );
}
